import { useState, useEffect, useRef, memo, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSend, FiTrash2, FiCopy, FiCpu, FiZap, FiStar, FiCheckCircle, 
  FiMenu, FiX, FiPlus, FiMessageCircle, FiUser, FiCompass, FiImage, 
  FiBarChart2, FiBookOpen, FiInfo, FiCamera, FiFolder, FiLock, FiClock,
  FiEye, FiGift   // 🆕
} from 'react-icons/fi';
import { FaRobot } from 'react-icons/fa';
import './ai.css';
import { useAuth } from '../context/AuthContext';
import { useCoins } from '../context/CoinContext.jsx';           // 🆕
import { useExams } from '../components/Exams/ExamContext.jsx';  // 🆕
import { engine } from '../core/books/storage.js';               // 🆕
import { getToken } from '../utils/secureStorage';               // 🆕
import {
  getDailyPlan,
  buildAction,
  getStepProgress,
  takeSnapshot,
  buildPendingItem,
} from './aiCoachEngine';                                       // 🆕
import SmartCoachModal from './SmartCoachModal';   // 🆕

// ============================================================
// ماژول‌های جدید: اشتراک + پرداخت
// ============================================================
import {
  PLAN_LIMITS, PLAN_PRICES,
  getSubscription, setSubscription,
  hasActivePlan, getUsage, saveUsage,
  incrementMessage, incrementImage,
  remainingMessages as calcRemainingMsg,
  remainingImages as calcRemainingImg,
  createBlankUsage,
} from '../core/ai/subscription';
import {
  getConversations as getServerConversations,
  saveConversation as saveServerConversation,
  deleteConversation as deleteServerConversation,
} from '../services/aiConversationService';
import {
  pay as payService,
  verifyPayment as verifyPaymentService,
  parseCallbackParams,
} from '../services/paymentService';

// ============================================================
// پیکربندی پلن‌ها
// ============================================================
const MODEL_CONFIGS = {
  simple: {
    name: 'ساده',
    limit: PLAN_LIMITS.simple.messages,       // 20
    imageUploadLimit: PLAN_LIMITS.simple.images, // 0
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1, #818cf8)',
    icon: <FiCpu />,
    badge: '⚡ پایه',
    temperature: 0.3,
    maxTokens: 150,
    description: 'سبک و سریع برای پاسخ‌های روزمره',
    features: ['پاسخ‌های سریع و مختصر', 'مصرف بهینه منابع', 'مناسب سوالات ساده درسی', 'بدون نیاز به اینترنت پرسرعت']
  },
  pro: {
    name: 'پرو',
    limit: PLAN_LIMITS.pro.messages,           // 50
    imageUploadLimit: PLAN_LIMITS.pro.images,  // 3
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
    icon: <FiZap />,
    badge: '🔥 حرفه‌ای',
    temperature: 0.5,
    maxTokens: 512,
    description: 'تعادل هوشمند بین سرعت و کیفیت',
    features: ['آپلود تصویر سوال (۱۰ بار در روز)', 'تحلیل عمیق وضعیت تحصیلی', 'برنامه‌ریزی هوشمند درسی', 'پشتیبانی از مکالمات طولانی']
  },
  ultimate: {
    name: 'ویژه',
    limit: PLAN_LIMITS.ultimate.messages,      // 300
    imageUploadLimit: PLAN_LIMITS.ultimate.images, // 10
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899, #f472b6)',
    icon: <FiStar />,
    badge: '💎 بی‌نهایت',
    temperature: 0.7,
    maxTokens: 1024,
    description: 'پیشرفته‌ترین دستیار آموزشی',
    features: ['آپلود نامحدود تصویر سوالات', 'پاسخ‌های دقیق و تشریحی', 'بدون محدودیت در تعداد پیام', 'دسترسی به جدیدترین قابلیت‌ها']
  }
};

const MAX_INPUT_CHARS = {
  simple: 500,
  pro: 2000,
  ultimate: 5000
};

const CONFIG = {
  modelText: 'gpt-4o-mini',
  baseURL: '/api/ai/chat',
};






// ============================================================
// 🆕 Coach Config — قیمت‌گذاری ساده
// ============================================================
// برای تغییر قیمت بدون کدنویسی، در Console مرورگر بزن:
// localStorage.setItem('coachConfig', '{"FIRST_REQUEST_COST":50,"REGENERATE_COST":150}')
// برای بازگشت به پیش‌فرض:
// localStorage.removeItem('coachConfig')
// ============================================================
const COACH_DEFAULT_CONFIG = {
  FIRST_REQUEST_COST: 0,   // 0 = اولین درخواست روزانه رایگان
  REGENERATE_COST: 50,    // هر درخواست اضافه در همان روز
  DAILY_REWARD_COINS: 100,  // 🆕 پاداش سکه برای تکمیل همه‌ی ماموریت‌های امروز
};

const COACH_CONFIG = (() => {
  try {
    const stored = JSON.parse(localStorage.getItem('coachConfig') || 'null');
    if (stored && typeof stored === 'object') {
      return {
        FIRST_REQUEST_COST: Number(stored.FIRST_REQUEST_COST ?? COACH_DEFAULT_CONFIG.FIRST_REQUEST_COST) || 0,
        REGENERATE_COST: Number(stored.REGENERATE_COST ?? COACH_DEFAULT_CONFIG.REGENERATE_COST) || 0,
        DAILY_REWARD_COINS: Number(stored.DAILY_REWARD_COINS ?? COACH_DEFAULT_CONFIG.DAILY_REWARD_COINS) || 0,  // 🆕
      };
    }
  } catch {}
  return { ...COACH_DEFAULT_CONFIG };
})();






// ============================================================
// 🆕 Coach — helpers + API + conversation formatter
// ============================================================
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const coachHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

const genId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const coachApi = {
  async getPlan(userId) {
    if (!userId) return null;
    try {
      let res = await fetch(
        `/api/db/aiCoachPlans?userId=${encodeURIComponent(userId)}&date=${todayStr()}`,
        { headers: coachHeaders() }
      );
      let data = res.ok ? await res.json() : null;

      // ✅ fallback: اگر کوئری خالی بود (اختلاف نوع userId)، همه‌ی پلن‌های امروز را بگیر و کلاینت‌ساید فیلتر کن
      if (!Array.isArray(data) || data.length === 0) {
        res = await fetch(
          `/api/db/aiCoachPlans?date=${todayStr()}`,
          { headers: coachHeaders() }
        );
        const all = res.ok ? await res.json() : [];
        data = Array.isArray(all)
          ? all.filter((r) => r && String(r.userId) === String(userId))
          : [];
      }

      if (!Array.isArray(data) || data.length === 0) return null;
      const sorted = [...data].sort(
        (a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0)
      );
      return sorted[0];
    } catch { return null; }
  },

  async savePlan(userId, plan, meta = {}) {
    if (!userId || !plan) return null;
    try {
      const res = await fetch('/api/db/aiCoachPlans', {
        method: 'POST',
        headers: coachHeaders(),
        body: JSON.stringify({
          id: genId('acp'),
          userId,
          date: todayStr(),
          plan,
          createdAt: Date.now(),
          generatedWith: meta.generatedWith || 'free',
          coinsSpent: meta.coinsSpent || 0,
          version: 3,
        }),
      });
      return res.ok ? await res.json() : null;
    } catch { return null; }
  },

  async updatePlan(planId, patch) {
    if (!planId) return null;
    try {
      const res = await fetch(`/api/db/aiCoachPlans/${planId}`, {
        method: 'PATCH',
        headers: coachHeaders(),
        body: JSON.stringify(patch),
      });
      return res.ok ? await res.json() : null;
    } catch { return null; }
  },

  async deleteOldPlans(userId) {
    if (!userId) return;
    try {
      const res = await fetch(
        `/api/db/aiCoachPlans?userId=${encodeURIComponent(userId)}`,
        { headers: coachHeaders() }
      );
      if (!res.ok) return;
      const all = await res.json();
      if (!Array.isArray(all)) return;
      const today = todayStr();
      for (const p of all) {
        if (p.date !== today) {
          await fetch(`/api/db/aiCoachPlans/${p.id}`, {
            method: 'DELETE',
            headers: coachHeaders(),
          }).catch(() => {});
        }
      }
    } catch {}
  },

  async getQuota(userId) {
    const empty = { _id: null, freeUsed: 0, paidCount: 0, totalCoinsSpent: 0 };
    if (!userId) return empty;
    try {
      let res = await fetch(
        `/api/db/aiCoachQuota?userId=${encodeURIComponent(userId)}&date=${todayStr()}`,
        { headers: coachHeaders() }
      );
      let data = res.ok ? await res.json() : null;

      // ✅ fallback: اگر کوئری خالی بود، همه‌ی سهمیه‌های امروز را بگیر و کلاینت‌ساید فیلتر کن
      if (!Array.isArray(data) || data.length === 0) {
        res = await fetch(
          `/api/db/aiCoachQuota?date=${todayStr()}`,
          { headers: coachHeaders() }
        );
        const all = res.ok ? await res.json() : [];
        data = Array.isArray(all)
          ? all.filter((r) => r && String(r.userId) === String(userId))
          : [];
      }

      if (!Array.isArray(data) || data.length === 0) return empty;
      const row = data[0];
      return {
        _id: row.id,
        freeUsed: Number(row.freeUsed) || 0,
        paidCount: Number(row.paidCount) || 0,
        totalCoinsSpent: Number(row.totalCoinsSpent) || 0,
      };
    } catch { return empty; }
  },

  async updateQuota(userId, patch) {
    if (!userId) return null;
    try {
      const existing = await coachApi.getQuota(userId);
      if (existing._id) {
        const res = await fetch(`/api/db/aiCoachQuota/${existing._id}`, {
          method: 'PATCH',
          headers: coachHeaders(),
          body: JSON.stringify(patch),
        });
        return res.ok ? await res.json() : null;
      }
      const res = await fetch('/api/db/aiCoachQuota', {
        method: 'POST',
        headers: coachHeaders(),
        body: JSON.stringify({
          id: genId('acq'),
          userId,
          date: todayStr(),
          freeUsed: 0,
          paidCount: 0,
          totalCoinsSpent: 0,
          ...patch,
        }),
      });
      return res.ok ? await res.json() : null;
    } catch { return null; }
  },
};

// 🆕 فرمت پلن به یک گفتگو برای ذخیره در سرور
const planToConversation = (plan, userId) => {
  if (!plan || !userId) return null;

  const persianDate = new Date().toLocaleDateString('fa-IR', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const analysis = plan.analysis || {};
  const strengths = Array.isArray(analysis.strengths) ? analysis.strengths : [];
  const weaknesses = Array.isArray(analysis.weaknesses) ? analysis.weaknesses : [];
  const suggestions = Array.isArray(plan.suggestions) ? plan.suggestions : [];
  const greeting = plan.greeting?.text || 'سلام!';

  const strengthsText = strengths.length > 0
    ? strengths.map((s, i) => `  ${i + 1}. ${s.title || ''} — ${s.reason || ''}`).join('\n')
    : '  ─';

  const weaknessesText = weaknesses.length > 0
    ? weaknesses.map((w, i) => `  ${i + 1}. ${w.title || ''} — ${w.reason || ''}`).join('\n')
    : '  ─';

  const missionsText = suggestions.length > 0
    ? suggestions.map((s, i) => {
        const duration = s.estimatedDuration || 15;
        const reason = s.why || s.reason || '';
        const urgent = (s.urgency || 0) >= 7 ? ' 🚨' : '';
        return `  ${i + 1}. ${s.title || 'پیشنهاد'}${urgent}\n     ⏱ ${duration} دقیقه${reason ? `\n     💬 ${reason}` : ''}`;
      }).join('\n\n')
    : '  امروز ماموریت خاصی نداری. استراحت کن!';

  const assistantContent = [
    greeting,
    '',
    '📊 تحلیل وضعیتت:',
    '',
    '💪 نقاط قوت:',
    strengthsText,
    '',
    '🎯 نیاز به توجه:',
    weaknessesText,
    '',
    '✨ ماموریت‌های امروز:',
    '',
    missionsText,
    '',
    '─────────────────',
    `📅 ${persianDate}`,
    'موفق باشی! 💪',
  ].join('\n');

  return {
    id: `coach_${userId}_${todayStr()}`,
    title: `🧠 مشاوره هوشمند — ${persianDate}`,
    messages: [
      {
        role: 'user',
        content: 'برنامه‌ی امروزم رو بده',
        timestamp: Date.now(),
      },
      {
        role: 'assistant',
        content: assistantContent,
        timestamp: Date.now(),
      },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    meta: {
      source: 'aiCoach',
      planDate: todayStr(),
      state: plan.meta?.state || null,
      mode: plan.meta?.mode || null,
    },
  };
};

// ============================================================
// ابزار: رزایز تصویر
// ============================================================
const resizeImage = (dataUrl, maxSize = 768) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      if (width > height) {
        if (width > maxSize) { height = Math.round((height * maxSize) / width); width = maxSize; }
      } else {
        if (height > maxSize) { width = Math.round((width * maxSize) / height); height = maxSize; }
      }
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    // ✅ اگر مرورگر نتوانست عکس را باز کند (مثل HEIC)، به جای برگرداندن فایل خام، یک تصویر JPEG کوچک خالی برمی‌گردانیم
    img.onerror = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1; canvas.height = 1;
      resolve(canvas.toDataURL('image/jpeg', 0.1));
    };
    img.src = dataUrl;
  });
};

// ============================================================
// ابزار: تاریخ انقضا به فارسی
// ============================================================
const formatExpiry = (expiresAt) => {
  if (!expiresAt) return '';
  try {
    return new Date(expiresAt).toLocaleDateString('fa-IR', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  } catch { return ''; }
};

// ============================================================
// Sidebar
// ============================================================
const SidebarComponent = memo(({ 
  isOpen, 
  onClose, 
  selectedPlan, 
  setSelectedPlan, 
  conversations, 
  activeConversationId, 
  setActiveConversationId, 
  deleteConversation, 
  renameConversation,
  activeTooltip,
  showTooltip,
  clearTooltipTimeout,
  hideTooltip,
  isPlanUnlocked,
  onBuyPlan,
  subscription,
}) => {
  if (!isOpen) return null;
  
  const handleSelectPlan = (planKey) => {
    if (!isPlanUnlocked(planKey)) {
      onBuyPlan(planKey);
      return;
    }
    setSelectedPlan(planKey);
    onClose();
  };
  
  const handleSelectConversation = (convId) => {
    setActiveConversationId(convId);
    onClose();
  };
  
  return (
    <>
      <motion.div 
        className="sidebar-overlay" 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        onClick={onClose}
      />
      <motion.div 
        className="sidebar" 
        initial={{ x: '-100%' }} 
        animate={{ x: 0 }} 
        exit={{ x: '-100%' }} 
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      >
        <div className="sidebar-header">
          <h2>✨ منوی هوشمند</h2>
          <motion.button 
            className="close-sidebar" 
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
          >
            <FiX />
          </motion.button>
        </div>
        <div className="scrollable-content">

          {/* نمایش وضعیت اشتراک فعال */}
          {subscription && (
            <div className="active-sub-banner">
              <FiCheckCircle />
              <div>
                <div className="active-sub-title">
                  پلن {MODEL_CONFIGS[subscription.plan]?.name} فعال است
                </div>
                <div className="active-sub-expire">
                  اعتبار تا: {formatExpiry(subscription.expiresAt)}
                </div>
              </div>
            </div>
          )}

          <div className="plan-selector-sidebar">
            <h3>🎯 نوع دستیار</h3>
            <div className="plan-cards">
              {Object.keys(MODEL_CONFIGS).map(key => {
                const unlocked = isPlanUnlocked(key);
                const active = selectedPlan === key;
                return (
                  <div 
                    key={key} 
                    className={`plan-card ${active ? 'active' : ''} ${!unlocked ? 'locked' : ''}`}
                    style={{ '--plan-color': MODEL_CONFIGS[key].color, '--plan-gradient': MODEL_CONFIGS[key].gradient }}
                    onClick={() => handleSelectPlan(key)}
                  >
                    <div className="plan-card-main">
                      <div className="plan-icon">{MODEL_CONFIGS[key].icon}</div>
                      <div className="plan-info-side">
                        <div className="plan-name">
                          {MODEL_CONFIGS[key].name}
                          {!unlocked && <FiLock className="lock-icon" />}
                        </div>







                        {/*<div className="plan-limit">
                          {MODEL_CONFIGS[key].limit === 999999 ? '∞' : MODEL_CONFIGS[key].limit} پیام/روز
                        </div>*/}









                        <div className="plan-badge-side">
                          {unlocked ? MODEL_CONFIGS[key].badge : '🔒 قفل'}
                        </div>
                      </div>
                    </div>
                    <div className="plan-info-wrapper">
                      <motion.button 
                        className="plan-info-btn" 
                        onClick={(e) => { e.stopPropagation(); showTooltip(key); }}
                        whileHover={{ scale: 1.1, rotate: 10 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiInfo />
                      </motion.button>
                      <AnimatePresence>
                        {activeTooltip === key && (
                          <motion.div 
                            className="plan-tooltip"
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            style={{ borderLeftColor: MODEL_CONFIGS[key].color }}
                            onMouseEnter={clearTooltipTimeout}
                            onMouseLeave={hideTooltip}
                          >
                            <div className="tooltip-header" style={{ background: MODEL_CONFIGS[key].gradient }}>
                              {MODEL_CONFIGS[key].icon}
                              <span>{MODEL_CONFIGS[key].name}</span>
                              <span className="tooltip-badge">{MODEL_CONFIGS[key].badge}</span>
                            </div>
                            <div className="tooltip-desc">{MODEL_CONFIGS[key].description}</div>
                            <div className="tooltip-features">
                              {MODEL_CONFIGS[key].features.map((feature, i) => (
                                <div key={i} className="tooltip-feature">
                                  <span className="tooltip-feature-icon">✨</span>
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>
                            {!unlocked && (
                              <div className="tooltip-buy-hint">
                                <FiLock />
                                <span>برای فعال‌سازی کلیک کن</span>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="sidebar-conversations">
            <h3>📜 تاریخچه گفتگوها</h3>
            {conversations.map(conv => (
              <motion.div 
                key={conv.id} 
                whileHover={{ scale: 1.02 }} 
                className={`conv-item ${activeConversationId === conv.id ? 'active' : ''}`} 
                onClick={() => handleSelectConversation(conv.id)}
                onDoubleClick={(e) => { e.stopPropagation(); const newTitle = prompt('عنوان جدید:', conv.title); if (newTitle) renameConversation(conv.id, newTitle); }}
              >
                <div className="conv-info">
                  <FiMessageCircle className="conv-icon" />
                  <div>
                    <div className="conv-title">{conv.title}</div>
                    <div className="conv-date">{new Date(conv.updatedAt).toLocaleDateString('fa-IR')}</div>
                  </div>
                </div>
                <motion.button 
                  className="conv-delete" 
                  onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id, e); }} 
                  whileHover={{ scale: 1.1 }}
                >
                  <FiTrash2 />
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
});

// ============================================================
// ChatInput
// ============================================================
const ChatInput = memo(({ 
  input, 
  setInput, 
  sendMessage, 
  isLoading, 
  remainingMessages,
  maxChars,
  canUploadImage,
  onImageClick
}) => {
  const textareaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    
    const computedStyle = getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    
    const minHeight = lineHeight + paddingTop + paddingBottom;
    const maxHeight = lineHeight * 6 + paddingTop + paddingBottom;

    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = newHeight + 'px';
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [input]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey || e.ctrlKey) return;
      if (isMobile) return;
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="input-bar" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
      <textarea
        ref={textareaRef}
        rows={1}
        value={input}
        onChange={e => {
          if (e.target.value.length <= maxChars) {
            setInput(e.target.value);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={`سوال خود را بنویسید... `}
        disabled={isLoading}
        maxLength={maxChars}
        className="chat-textarea"
        style={{
          flex: 1,
          resize: 'none',
          padding: '10px 22px',
          borderRadius: '60px',
          border: 'none',
          background: 'rgba(255, 255, 255, 0.07)',
          color: 'white',
          fontSize: '1rem',
          outline: 'none',
          fontFamily: 'inherit',
          lineHeight: '1.5',
          maxHeight: 'calc(1.5rem * 6 + 20px)',
          transition: 'all 0.25s ease',
          fontWeight: '450',
        }}
      />
      {canUploadImage && (
        <motion.button
          type="button"
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.05 }}
          className="image-action-btn"
          onClick={onImageClick}
          disabled={isLoading}
          title="آپلود تصویر"
        >
          <FiImage />
        </motion.button>
      )}
      <motion.button 
        whileTap={{ scale: 0.9 }} 
        className="send-action" 
        onClick={sendMessage} 
        disabled={isLoading || remainingMessages === 0} 
        animate={!isLoading && input.trim() ? { scale: [1, 1.05, 1], transition: { repeat: Infinity, duration: 1.5 } } : {}}
      >
        <FiSend />
      </motion.button>
    </div>
  );
});

// ============================================================
// MessageItem
// ============================================================
const MessageItem = memo(({ msg, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 50, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.4, delay: idx * 0.05, type: 'spring', stiffness: 200, damping: 20 }}
    className={`message ${msg.role === 'user' ? 'user' : 'assistant'}`}
    whileHover={{ scale: 1.01 }}
  >
    <div className="message-avatar">
      {msg.role === 'user' ? <FiUser /> : <div className="ai-spin"><FaRobot /></div>}
    </div>
    <div className="message-bubble">
      {msg.hasImage && <div className="user-image-preview"><img src={typeof msg.content === 'object' ? msg.content[1]?.image_url?.url : ''} alt="uploaded" /></div>}
      <div className="message-text">{typeof msg.content === 'string' ? msg.content : msg.content[0]?.text}</div>
      <button className="copy-message" onClick={() => {
        const text = typeof msg.content === 'string' ? msg.content : msg.content[0]?.text;
        navigator.clipboard.writeText(text);
        const toast = document.createElement('div');
        toast.className = 'copy-toast';
        toast.innerText = '✨ کپی شد! ✨';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 1500);
      }}><FiCopy /></button>
    </div>
  </motion.div>
));

// ============================================================
// PaymentModal
// ============================================================
const PaymentModal = memo(({ plan, period, setPeriod, onPay, onClose, isPaying }) => {
  const cfg = MODEL_CONFIGS[plan];
  const periods = PLAN_PRICES[plan];
  if (!cfg || !periods) return null;

  return (
    <>
      <motion.div 
        className="payment-overlay" 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        onClick={onClose}
      />
      <motion.div
        className="payment-modal"
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.96 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        <div className="payment-header" style={{ background: cfg.gradient }}>
          {cfg.icon}
          <span>فعال‌سازی پلن {cfg.name}</span>
          <button className="payment-close" onClick={onClose} disabled={isPaying}>
            <FiX />
          </button>
        </div>
        <div className="payment-body">
          <p className="payment-desc">{cfg.description}</p>

          <div className="payment-features">
            {cfg.features.map((f, i) => (
              <div key={i} className="payment-feature">
                <FiCheckCircle /> <span>{f}</span>
              </div>
            ))}
          </div>

          <div className="payment-periods">
            {Object.keys(periods).map(p => (
              <button
                key={p}
                className={`payment-period ${period === p ? 'active' : ''}`}
                onClick={() => setPeriod(p)}
                disabled={isPaying}
              >
                <div className="payment-period-label">{periods[p].label}</div>
                <div className="payment-period-price">
                  {periods[p].amount.toLocaleString('fa-IR')}
                </div>
                <div className="payment-period-unit">تومان</div>
                {p === 'monthly' && <div className="payment-period-badge">پیشنهاد</div>}
              </button>
            ))}
          </div>

          <div className="payment-total">
            <FiClock /> مبلغ قابل پرداخت:&nbsp;
            <b>{periods[period].amount.toLocaleString('fa-IR')} تومان</b>
          </div>
        </div>
        <div className="payment-footer">
          <button className="payment-cancel" onClick={onClose} disabled={isPaying}>
            انصراف
          </button>
          <button 
            className="payment-pay" 
            onClick={onPay} 
            disabled={isPaying} 
            style={{ background: cfg.gradient }}
          >
            {isPaying ? 'در حال انتقال...' : 'پرداخت و فعال‌سازی'}
          </button>
        </div>
      </motion.div>
    </>
  );
});

// ============================================================
// AIPage - کامپوننت اصلی
// ============================================================
export default function AIPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { coins, xp, removeCoins, setUserRewards } = useCoins();  // 🆕
  const { exams } = useExams();                                    // 🆕
  const userId = user?.id || 'guest';

  // 🆕 مشاور هوشمند فقط برای دانش‌آموزان
  const canUseCoach = user?.role === 'student';


  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyOverscroll = body.style.overscrollBehavior;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';   // 🆕 جلوگیری از overscroll روی iOS

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, []);

  // ===== بستن صفحه AI =====
  // اگه کاربر از داخل اپ اومده → برگرد به صفحه‌ی قبلی
  // اگه مستقیم وارد شده → برو به خانه (جلوگیری از صفحه‌ی سیاه)
  const handleCloseAI = useCallback(() => {
    if (location.key === 'default' || window.history.length <= 1) {
      // مستقیم وارد شده → fallback
      const role = user?.role || 'student';
      const fallback =
        role === 'teacher' ? '/TeacherHome' :
        role === 'admin'   ? '/AdminHome' :
        role === 'parent'  ? '/ParentHome' :
        '/StudentHome';
      navigate(fallback, { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate, user?.role]);

  // --- ابزار کلید اختصاصی ---
  const getStorageKey = useCallback((baseKey) => `${baseKey}_${userId}`, [userId]);

  // --- state های اصلی ---
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('simple');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [showImageMenu, setShowImageMenu] = useState(false);
const [showCoachModal, setShowCoachModal] = useState(false);

// 🆕 Coach states
const [todayPlan, setTodayPlan] = useState(null);
const [todayPlanId, setTodayPlanId] = useState(null);
const [coachQuota, setCoachQuota] = useState({ freeUsed: 0, paidCount: 0, totalCoinsSpent: 0 });
const [coachStats, setCoachStats] = useState(null);
const [coachProgress, setCoachProgress] = useState({});
const [isGenerating, setIsGenerating] = useState(false);
const [isCoachPaying, setIsCoachPaying] = useState(false);
const [dailyRewardClaimed, setDailyRewardClaimed] = useState(() => {  // 🆕
  try {
    if (!user?.id) return false;
    return !!localStorage.getItem(`aiCoach_rewarded_${user.id}_${todayStr()}`);
  } catch { return false; }
});
const lastCoachDateRef = useRef(todayStr());

// 🆕 Auto-trigger refs (برای کلیک خودکار از StudentHome)
const coachBootstrappedRef = useRef(false);
const coachAutoTriggeredRef = useRef(null);
const handleOpenCoachRef = useRef(null);
const [coachAutoClicking, setCoachAutoClicking] = useState(false);

  // --- state های جدید: اشتراک + مصرف + پرداخت ---
const [subscription, setSubscriptionState] = useState(null);
const [usage, setUsage] = useState(() => createBlankUsage());
const [showPaymentModal, setShowPaymentModal] = useState(null);
const [payingPeriod, setPayingPeriod] = useState('monthly');
const [isPaying, setIsPaying] = useState(false);
const [isSubLoading, setIsSubLoading] = useState(true);

  const tooltipTimeoutRef = useRef(null);
  const galleryInputRef = useRef(null);
  const imageMenuRef = useRef(null);

  // --- گفتگوها: مقدار اولیه ---
const [conversations, setConversations] = useState([]);
const [activeConversationId, setActiveConversationId] = useState(null);
const [convsLoading, setConvsLoading] = useState(true);

  const currentPlan = MODEL_CONFIGS[selectedPlan];

  // --- مقادیر مشتق‌شده از usage ---
const remainingMessages = useMemo(
  () => calcRemainingMsg(selectedPlan, usage),
  [selectedPlan, usage]
);
const remainingImageUploads = useMemo(
  () => calcRemainingImg(selectedPlan, usage),
  [selectedPlan, usage]
);
  const canUploadImage = currentPlan.imageUploadLimit > 0;

  // --- بررسی قفل بودن پلن ---
const isPlanUnlocked = useCallback(
  (planKey) => {
    if (planKey === 'simple') return true;
    return !!subscription && subscription.plan === planKey;
  },
  [subscription]
);

  // ============================================================
  // اثر ۱: هر وقت کاربر عوض شد، همه‌چیز رو ری‌لود کن
  // ============================================================
useEffect(() => {
  if (!userId) return;
  let cancelled = false;

  const load = async () => {
    setIsSubLoading(true);
    setConvsLoading(true);

    // ۱. اشتراک + مصرف (موازی)
    const [sub, use] = await Promise.all([
      getSubscription(userId),
      getUsage(userId),
    ]);
    if (cancelled) return;
    setSubscriptionState(sub);
    setUsage(use);

    // ۲. گفتگوها
    let convs = await getServerConversations(userId);
    if (cancelled) return;

    // اگر هیچ گفتگویی نبود → یک گفتگوی خالی بساز
    if (convs.length === 0) {
      // 🔧 FIX: تولید id یکتا (نه 'default' ثابت)
      const uniqueId = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      const initial = {
        id: uniqueId,
        title: 'خوش آمدید',
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      convs = [initial];
      // ذخیره در سرور (background)
      saveServerConversation(userId, initial).catch(() => {});
    }

    setConversations(convs);
    setActiveConversationId(convs[0]?.id || null);

    // اگر پلن فعلی دیگه مجاز نیست → simple
    if (selectedPlan !== 'simple' && !sub) {
      setSelectedPlan('simple');
    }

    setIsSubLoading(false);
    setConvsLoading(false);
  };

  load();
  return () => { cancelled = true; };
}, [userId]);

  // ============================================================
  // اثر ۲: ذخیره‌ی خودکار گفتگوها به ازای کاربر
  // ============================================================
// (حذف شد)
// ذخیره‌ی خودکار هر گفتگو در سرور هنگام تغییر conversations
useEffect(() => {
  if (!userId) return;
  if (!conversations.length) return;
  // هر گفتگویی که تغییر کرده را به سرور بفرست
  const activeConv = conversations.find((c) => c.id === activeConversationId);
  if (activeConv) {
    saveServerConversation(userId, activeConv).catch(() => {});
  }
}, [conversations, activeConversationId, userId]);

  // ============================================================
  // اثر ۳: اگر پلن فعلی قفل شد (اشتراک منقضی)، برگرد به simple
  // ============================================================
  useEffect(() => {
    if (selectedPlan === 'simple') return;
    // 🔧 FIX: hasActivePlan async است — از state subscription استفاده کن
    const stillValid = subscription && subscription.plan === selectedPlan;
    if (!stillValid) {
      setSelectedPlan('simple');
    }
  }, [subscription, selectedPlan]);

  // ============================================================
  // اثر ۴: بازگشت از درگاه پرداخت
  // ============================================================
useEffect(() => {
  const { paymentId, authority, status } = parseCallbackParams();
  if (!paymentId || !authority) return;
  if (String(status).toUpperCase() === 'NOK') {
    alert('❌ پرداخت توسط کاربر لغو شد.');
    window.history.replaceState({}, '', window.location.pathname);
    return;
  }

  const pendingRaw = sessionStorage.getItem('pendingPayment');
  if (!pendingRaw) return;

  let pending;
  try { pending = JSON.parse(pendingRaw); } catch { return; }

  (async () => {
    try {
      const result = await verifyPaymentService({ paymentId, authority, status });

      if (result && result.success) {
        // سرور در metadata نتیجه، plan و months را برگردانده
        const meta = result.metadata || {};
        const planName = meta.plan || pending.plan;
        const days = meta.days || pending.days || 30;
        const expiresAt = Date.now() + days * 24 * 60 * 60 * 1000;

        // استخراج نام پلن اصلی (pro/ultimate) از plan
        let basePlan = planName;
        if (planName.startsWith('pro')) basePlan = 'pro';
        else if (planName.startsWith('ultimate')) basePlan = 'ultimate';

        const sub = {
          plan: basePlan,
          planRaw: planName,
          startedAt: Date.now(),
          expiresAt,
          refId: result.refId || null,
        };
        await setSubscription(userId, sub);
        setSubscriptionState(sub);
        setSelectedPlan(basePlan);
        alert('✅ پرداخت با موفقیت انجام شد و پلن فعال شد.');
      } else {
        alert('❌ پرداخت تایید نشد: ' + (result?.message || 'خطای نامشخص'));
      }
    } catch (e) {
      alert('خطا در تایید پرداخت: ' + (e?.message || ''));
    } finally {
      sessionStorage.removeItem('pendingPayment');
      window.history.replaceState({}, '', window.location.pathname);
    }
  })();
}, [userId]);

// ============================================================
// 🆕 اثر ۵: Bootstrap مشاور + پاک‌سازی رکوردهای دیروز
// ============================================================
useEffect(() => {
  coachBootstrappedRef.current = false;   // 🆕 reset on user change
  if (!canUseCoach) return;               // 🆕 فقط دانش‌آموزان
  if (!user?.id) return;
  let cancelled = false;

  const bootstrap = async () => {
    try { await coachApi.deleteOldPlans(user.id); } catch {}

    // پاک‌سازی pendingهای قدیمی‌تر از ۲۴ ساعت
    try {
      const raw = localStorage.getItem(`aiCoach_pending_${user.id}`);
      if (raw) {
        const pending = JSON.parse(raw);
        const cutoff = Date.now() - 24 * 3600 * 1000;
        let changed = false;
        Object.keys(pending).forEach((id) => {
          if (!pending[id]?.clickTime || pending[id].clickTime < cutoff) {
            delete pending[id];
            changed = true;
          }
        });
        if (changed) {
          localStorage.setItem(`aiCoach_pending_${user.id}`, JSON.stringify(pending));
        }
      }
    } catch {}

    const [planRecord, quota] = await Promise.all([
      coachApi.getPlan(user.id),
      coachApi.getQuota(user.id),
    ]);
    if (cancelled) return;

    setTodayPlan(planRecord?.plan || null);
    setTodayPlanId(planRecord?.id || null);
    setCoachQuota(quota);
    try { setCoachProgress(getStepProgress(user.id) || {}); } catch {}
    coachBootstrappedRef.current = true;   // 🆕 mark bootstrap done
  };

  bootstrap();
  return () => { cancelled = true; };
}, [user?.id, canUseCoach]);

// ============================================================
// 🆕 اثر ۶: چک نیمه‌شب (هر ۶۰ ثانیه)
// ============================================================
useEffect(() => {
  if (!canUseCoach) return;               // 🆕 فقط دانش‌آموزان
  if (!user?.id) return;
  const interval = setInterval(() => {
    const newToday = todayStr();
    if (newToday !== lastCoachDateRef.current) {
      lastCoachDateRef.current = newToday;
      coachApi.deleteOldPlans(user.id).catch(() => {});
      setTodayPlan(null);
      setTodayPlanId(null);
      setCoachQuota({ freeUsed: 0, paidCount: 0, totalCoinsSpent: 0 });
      setCoachProgress({});
      setShowCoachModal(false);
      setDailyRewardClaimed(false); // 🆕 ریست پاداش برای روز جدید
    }
  }, 60000);
  return () => clearInterval(interval);
}, [user?.id, canUseCoach]);

// ============================================================
// 🆕 اثر ۷: محاسبه‌ی آمار Coach
// ============================================================
useEffect(() => {
  if (!canUseCoach) return;               // 🆕 فقط دانش‌آموزان
  if (!user?.id) return;

  const weekly = (() => {
    const arr = [0, 0, 0, 0, 0, 0, 0];
    const daily = user.dailyProgress || {};
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      arr[6 - i] = Number(daily[key]) || 0;
    }
    return arr;
  })();

  const firstHalf = weekly.slice(0, 3).reduce((a, b) => a + b, 0);
  const secondHalf = weekly.slice(3).reduce((a, b) => a + b, 0);
  const growth = firstHalf > 0
    ? Math.round(((secondHalf - firstHalf) / firstHalf) * 100)
    : (secondHalf > 0 ? 100 : 0);

  let streak = 0;
  try {
    const s = engine.get(`streak_${user.id}`, null);
    if (s) {
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      const last = s.lastDate || s.lastActivityDate;
      if (last === today || last === yesterday) streak = Number(s.streak) || 0;
    }
  } catch {}

  let examAvg = 0, examCount = 0;
  (exams || []).forEach((e) => {
    if (!e || e.isChallenge || e.isStepQuiz || e.statusType === 'archived') return;
    const r = e.results?.find((x) => String(x.studentId) === String(user.id));
    if (!r || r.isFinalSubmit !== true || r.isAbsent === true) return;
    const max = (e.questions || []).reduce((s, q) => s + (Number(q.score) || 1), 0);
    if (max <= 0) return;
    examAvg += (Number(r.score) / max) * 100;
    examCount++;
  });
  if (examCount > 0) examAvg = Math.round(examAvg / examCount);

  let medals = 0, almostMedals = 0;
  try {
    const a = JSON.parse(localStorage.getItem(`achievements_${user.id}`) || '{"unlocked":[],"progress":{}}');
    medals = (a.unlocked || []).length;
    almostMedals = Object.entries(a.progress || {})
      .filter(([, p]) => Number(p) >= 75 && Number(p) < 100).length;
  } catch {}

  setCoachStats({
    level: Math.floor(xp / 100) + 1,
    xpInLevel: xp % 100,
    stars: Math.min(5, Math.floor((xp % 100) / 20) + 1),
    streak, weekly, growth,
    examAvg, examCount,
    medals, almostMedals,
  });
}, [user, exams, xp, canUseCoach]);

// ============================================================
// 🆕 اثر ۸-الف: همگام‌سازی dailyRewardClaimed با localStorage
// ============================================================
useEffect(() => {
  if (!user?.id) { setDailyRewardClaimed(false); return; }
  try {
    const flag = !!localStorage.getItem(`aiCoach_rewarded_${user.id}_${todayStr()}`);
    setDailyRewardClaimed(flag);
  } catch {
    setDailyRewardClaimed(false);
  }
}, [user?.id]);

// ============================================================
// 🆕 اثر ۸-ب: پرداخت پاداش سکه پس از تکمیل همه‌ی ماموریت‌ها
// ============================================================
useEffect(() => {
  if (!canUseCoach) return;               // 🆕 فقط دانش‌آموزان
  if (!user?.id) return;
  if (!todayPlan) return;

  const suggestions = Array.isArray(todayPlan.suggestions) ? todayPlan.suggestions : [];
  if (suggestions.length === 0) return;

  const allDone = suggestions.every((s) => s?.id && coachProgress[s.id] === true);
  if (!allDone) return;

  const rewardKey = `aiCoach_rewarded_${user.id}_${todayStr()}`;
  if (localStorage.getItem(rewardKey)) {
    setDailyRewardClaimed(true);
    return;
  }

  const rewardAmount = Number(COACH_CONFIG.DAILY_REWARD_COINS) || 0;
  if (rewardAmount <= 0) return;

  try {
    // ✅ اول flag را بگذار تا حتی اگر setUserRewards کرش کند، دوباره واریز نشود
    localStorage.setItem(rewardKey, '1');
    setUserRewards((Number(coins) || 0) + rewardAmount, Number(xp) || 0);
    setDailyRewardClaimed(true);
  } catch (e) {
    // rollback در صورت شکست
    try { localStorage.removeItem(rewardKey); } catch {}
    console.warn('[coach] daily reward failed:', e);
  }
}, [user?.id, todayPlan, coachProgress, coins, xp, setUserRewards, canUseCoach]);

  // ============================================================
  // تولتیپ
  // ============================================================
  const showTooltip = useCallback((planKey) => {
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    setActiveTooltip(prev => prev === planKey ? null : planKey);
  }, []);
  
  const hideTooltip = useCallback(() => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setActiveTooltip(null);
    }, 300);
  }, []);
  
  const clearTooltipTimeout = useCallback(() => {
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
  }, []);

  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const toggleImageMenu = useCallback(() => setShowImageMenu(prev => !prev), []);
  const closeImageMenu = useCallback(() => setShowImageMenu(false), []);

  // ============================================================
  // بستن منوی تصویر با کلیک خارج
  // ============================================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (imageMenuRef.current && !imageMenuRef.current.contains(event.target)) {
        setShowImageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================
  // بارگذاری پیام‌های گفتگوی فعال
  // ============================================================
  useEffect(() => {
    const activeConv = conversations.find(c => c.id === activeConversationId);
    if (activeConv) setMessages(activeConv.messages);
  }, [activeConversationId, conversations]);

  // ============================================================
  // به‌روزرسانی پیام‌ها
  // ============================================================
  const updateMessages = useCallback((newMessages) => {
    setMessages(newMessages);
    setConversations(prev => prev.map(conv =>
      conv.id === activeConversationId ? { ...conv, messages: newMessages, updatedAt: new Date().toISOString() } : conv
    ));
  }, [activeConversationId]);

const newConversation = useCallback(() => {
  // 🔧 FIX: id یکتا با timestamp + random
  const newId = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  const newConv = {
    id: newId,
    title: 'گفتگوی جدید',
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  setConversations(prev => [newConv, ...prev]);
  setActiveConversationId(newId);
  setUploadedImage(null);
  setImagePreview(null);
  // ذخیره در سرور
  saveServerConversation(userId, newConv).catch(() => {});
}, [userId]);

const deleteConversation = useCallback((id, e) => {
  e.stopPropagation();
  if (conversations.length === 1) return;
  const newList = conversations.filter(c => c.id !== id);
  setConversations(newList);
  if (activeConversationId === id) setActiveConversationId(newList[0].id);
  // حذف از سرور
  deleteServerConversation(userId, id).catch(() => {});
}, [conversations, activeConversationId, userId]);

const renameConversation = useCallback((id, newTitle) => {
  if (!newTitle?.trim()) return;
  const trimmed = newTitle.trim();
  setConversations(prev => {
    const updated = prev.map(conv => conv.id === id ? { ...conv, title: trimmed } : conv);
    // ذخیره در سرور
    const target = updated.find(c => c.id === id);
    if (target) saveServerConversation(userId, target).catch(() => {});
    return updated;
  });
}, [userId]);

  // ============================================================
  // تولید عنوان خودکار
  // ============================================================
  const generateTitle = useCallback(async (firstMsg) => {
    try {
      const res = await fetch(CONFIG.baseURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: CONFIG.modelText,
          messages: [
            { role: 'system', content: 'خلاصه‌ای کوتاه (حداکثر ۵ کلمه) از پیام کاربر بنویس، فقط عنوان.' },
            { role: 'user', content: firstMsg }
          ],
          temperature: 0.3, max_tokens: 20
        })
      });
      const data = await res.json();
      return data.choices[0].message.content.replace(/["']/g, '').trim();
    } catch { return firstMsg.substring(0, 30); }
  }, []);

  // ============================================================
  // آپلود از گالری
  // ============================================================
  const handleGalleryUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file && canUploadImage) {
      if (remainingImageUploads <= 0) {
        alert(`محدودیت روزانه آپلود تصویر برای ${currentPlan.name} تمام شد!`);
        return;
      }
      const reader = new FileReader();
reader.onloadend = async () => {
  try {
    const resizedDataUrl = await resizeImage(reader.result, 768);
    setImagePreview(resizedDataUrl);
    setUploadedImage(file);
    setShowImageMenu(false);
  } catch (err) {
    // ✅ به جای ارسال فایل خام (که ممکن است HEIC باشد)، به کاربر اطلاع می‌دهیم
    alert('فرمت تصویر پشتیبانی نمی‌شود. لطفاً از یک تصویر JPEG یا PNG استفاده کنید.');
  }
};
      reader.readAsDataURL(file);
    } else if (!canUploadImage) {
      alert('قابلیت آپلود تصویر فقط در پلن‌های پرو و ویژه فعال است.');
    }
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  }, [canUploadImage, remainingImageUploads, currentPlan.name]);

  // ============================================================
  // آپلود با دوربین
  // ============================================================
  const handleCameraCapture = useCallback(async () => {
    if (!canUploadImage) {
      alert('قابلیت آپلود تصویر فقط در پلن‌های پرو و ویژه فعال است.');
      return;
    }
    if (remainingImageUploads <= 0) {
      alert(`محدودیت روزانه آپلود تصویر برای ${currentPlan.name} تمام شد!`);
      return;
    }
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) {
      alert('📱 این قابلیت فقط در گوشی موبایل قابل استفاده است.');
      setShowImageMenu(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
  video: { 
    facingMode: 'environment',
    width: { ideal: 1920 },   // 🆕 درخواست رزولوشن Full HD
    height: { ideal: 1080 }   // 🆕 درخواست رزولوشن Full HD
  } 
});
      const video = document.createElement('video');
      video.srcObject = stream;
      video.setAttribute('playsinline', '');
      video.style.position = 'fixed';
      video.style.top = '0';
      video.style.left = '0';
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      video.style.zIndex = '2000';
      video.style.background = '#000';
      document.body.appendChild(video);
      await video.play();
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const captureBtn = document.createElement('button');
      captureBtn.innerHTML = '📸 گرفتن عکس';
      captureBtn.style.position = 'fixed';
      captureBtn.style.bottom = '30px';
      captureBtn.style.left = '50%';
      captureBtn.style.transform = 'translateX(-50%)';
      captureBtn.style.background = 'linear-gradient(135deg, #6366f1, #818cf8)';
      captureBtn.style.border = 'none';
      captureBtn.style.color = 'white';
      captureBtn.style.padding = '14px 32px';
      captureBtn.style.borderRadius = '60px';
      captureBtn.style.fontSize = '1.1rem';
      captureBtn.style.fontWeight = 'bold';
      captureBtn.style.zIndex = '2001';
      captureBtn.style.cursor = 'pointer';
      document.body.appendChild(captureBtn);
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '❌ بستن';
      closeBtn.style.position = 'fixed';
      closeBtn.style.top = '20px';
      closeBtn.style.right = '20px';
      closeBtn.style.background = 'rgba(0,0,0,0.7)';
      closeBtn.style.border = 'none';
      closeBtn.style.color = 'white';
      closeBtn.style.padding = '10px 20px';
      closeBtn.style.borderRadius = '40px';
      closeBtn.style.fontSize = '0.9rem';
      closeBtn.style.zIndex = '2001';
      closeBtn.style.cursor = 'pointer';
      document.body.appendChild(closeBtn);
      const stopCamera = () => {
        stream.getTracks().forEach(track => track.stop());
        video.remove();
        captureBtn.remove();
        closeBtn.remove();
      };
      captureBtn.onclick = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
          const reader = new FileReader();
          reader.onloadend = async () => {
            // 🆕 فشرده‌سازی فوری عکس دوربین (مثل گالری) برای کاهش شدید مصرف توکن
            const resizedDataUrl = await resizeImage(reader.result, 768);
            setImagePreview(resizedDataUrl);
            setUploadedImage(file);
            setShowImageMenu(false);
          };
          reader.readAsDataURL(file);
          stopCamera();
        }, 'image/jpeg', 0.9);
      };
      closeBtn.onclick = stopCamera;
    } catch (error) {
      alert('❌ دسترسی به دوربین امکان‌پذیر نیست.');
      setShowImageMenu(false);
    }
  }, [canUploadImage, remainingImageUploads, currentPlan.name]);

  const removeImage = useCallback(() => {
    setUploadedImage(null);
    setImagePreview(null);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  }, []);

  // ============================================================
  // ارسال پیام
  // ============================================================
  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed && !uploadedImage) return;

    if (remainingMessages <= 0) {
      if (selectedPlan === 'simple') {
        alert('محدودیت روزانه‌ی پلن ساده تمام شد! برای ادامه، پلن پرو یا ویژه را فعال کن.');
      } else {
        alert(`محدودیت روزانه‌ی پلن ${currentPlan.name} تمام شد!`);
      }
      return;
    }

    if (uploadedImage && remainingImageUploads <= 0) {
      alert(`محدودیت روزانه آپلود تصویر برای ${currentPlan.name} تمام شد!`);
      return;
    }

    const userContent = uploadedImage && canUploadImage
      ? [
          { type: 'text', text: trimmed || 'لطفاً این سوال را حل کن:' },
          { type: 'image_url', image_url: { url: imagePreview, detail: 'low' } }
        ]
      : trimmed;

    const userMsg = { role: 'user', content: userContent, timestamp: Date.now(), hasImage: !!uploadedImage };
    const newMessages = [...messages, userMsg];
    updateMessages(newMessages);
    setInput('');
    const hadImage = !!uploadedImage;
    setUploadedImage(null);
    setImagePreview(null);
    setIsLoading(true);

    const isFirst = messages.length === 0 && trimmed;
    if (isFirst) {
      const newTitle = await generateTitle(trimmed);
      if (newTitle) setConversations(prev => prev.map(conv => conv.id === activeConversationId ? { ...conv, title: newTitle } : conv));
    }

    try {
      const response = await fetch(CONFIG.baseURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: CONFIG.modelText,
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          temperature: currentPlan.temperature,
          max_tokens: currentPlan.maxTokens
        })
      });
      if (!response.ok) throw new Error('خطا در ارتباط با سرور');
      const data = await response.json();
      const assistantMsg = { role: 'assistant', content: data.choices[0].message.content, timestamp: Date.now() };
      updateMessages([...newMessages, assistantMsg]);

      // ✅ ثبت مصرف به ازای کاربر
// ✅ ثبت مصرف در سرور (async)
try {
  if (hadImage) {
    const afterImg = await incrementImage(userId, selectedPlan);
    setUsage({ ...afterImg });
  } else {
    const afterMsg = await incrementMessage(userId, selectedPlan);
    setUsage({ ...afterMsg });
  }
} catch (e) {
  console.warn('⚠️ خطا در ثبت مصرف:', e);
}
    } catch (err) {
      console.error(err);
      alert(`خطا: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [input, uploadedImage, imagePreview, remainingMessages, remainingImageUploads, currentPlan, messages, updateMessages, generateTitle, activeConversationId, canUploadImage, selectedPlan, userId]);

  // ============================================================
  // منوی آپلود تصویر — به‌صورت JSX معمولی (نه کامپوننت)
  // ============================================================
  const imageUploadMenuJsx = (
    <AnimatePresence>
      {showImageMenu && canUploadImage && (
        <>
          <motion.div className="image-menu-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeImageMenu} />
        <motion.div 
          className="image-menu-center-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div className="image-upload-menu" ref={imageMenuRef} initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }}>
            <div className="image-menu-header"><FiImage /><span>انتخاب تصویر</span></div>
            <div className="image-menu-options">
              <motion.div className="image-menu-option camera-option" whileHover={{ scale: 1.02, x: 5 }} whileTap={{ scale: 0.98 }} onClick={handleCameraCapture}><FiCamera /><span>دوربین</span></motion.div>
              <label 
  className="image-menu-option gallery-option" 
  style={{ display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', width: '100%' }}
>
  <FiFolder /><span>گالری</span>
  <input 
    ref={galleryInputRef} 
    type="file" 
    accept="image/jpeg,image/png,image/webp" 
    onChange={handleGalleryUpload} 
    style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }} 
  />
</label>
            </div>
            <div className="image-menu-footer"><button className="image-menu-cancel" onClick={closeImageMenu}>انصراف</button></div>
          </motion.div>
        </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // ============================================================
  // هندلر خرید پلن
  // ============================================================
  const handleBuyPlan = useCallback((planKey) => {
    setShowPaymentModal({ plan: planKey });
    setPayingPeriod('monthly');
    setIsSidebarOpen(false);
  }, []);

const handlePay = useCallback(async () => {
  if (!showPaymentModal) return;
  const planKey = showPaymentModal.plan;
  const cfg = PLAN_PRICES[planKey]?.[payingPeriod];
  if (!cfg) return;

  setIsPaying(true);
  try {
    // نام پلن canonical که سرور می‌شناسد
    const planId = `${planKey}-${payingPeriod}`; // مثل: pro-monthly

    // ذخیره‌ی pending قبل از هدایت (برای زمان بازگشت از درگاه)
    sessionStorage.setItem('pendingPayment', JSON.stringify({
      plan: planKey,
      planId,
      period: payingPeriod,
      amount: cfg.amount,
      days: cfg.days,
    }));

    // فراخوانی سرویس پرداخت (startPayment خودش redirect می‌کند)
    await payService.aiSubscription({
      userId,
      plan: planId,
      months: payingPeriod === 'yearly' ? 12 : payingPeriod === 'monthly' ? 1 : 0,
    });
    // بعد از این خط کاربر به درگاه می‌رود
  } catch (e) {
    alert(e?.message || 'خطا در اتصال به درگاه');
    setIsPaying(false);
    sessionStorage.removeItem('pendingPayment');
  }
}, [showPaymentModal, payingPeriod, userId]);







// ============================================================
// 🆕 باز کردن مودال مشاور (مسیریابی هوشمند) + ذخیره به‌عنوان گفتگو
// ============================================================
const handleOpenCoach = useCallback(async () => {
  if (!user?.id) return;
  if (isGenerating) return;

  try { setCoachProgress(getStepProgress(user.id) || {}); } catch {}

  // ① پلن موجود → فقط باز کن
  if (todayPlan) {
    setShowCoachModal(true);
    return;
  }

  // ② قبلاً درخواست داده → تلاش برای بازخوانی از سرور
  if (coachQuota.freeUsed >= 1) {
    try {
      const existing = await coachApi.getPlan(user.id);
      if (existing?.plan) {
        setTodayPlan(existing.plan);
        setTodayPlanId(existing.id);
        setShowCoachModal(true);
        return;
      }
    } catch {}
    setShowCoachModal(true);
    return;
  }

  // ③ درخواست اول — بررسی قیمت
  const firstCost = Number(COACH_CONFIG.FIRST_REQUEST_COST) || 0;
  if (firstCost > 0 && coins < firstCost) {
    alert(`برای دریافت مشاوره به ${firstCost.toLocaleString('fa-IR')} سکه نیاز داری. موجودی: ${coins.toLocaleString('fa-IR')} سکه.`);
    return;
  }

  const startTime = Date.now();
  setIsGenerating(true);
  setShowCoachModal(true);

  const originalCoins = coins;
  const originalXp = xp;
  let coinsDeducted = false;

  try {
    if (firstCost > 0) {
      await new Promise((r) => setTimeout(r, 300));
      const spent = removeCoins(firstCost);
      if (spent !== firstCost) throw new Error('خطا در کسر سکه');
      coinsDeducted = true;
    }

    const newPlan = await getDailyPlan(user.id, user.grade, user, exams, { forceRefresh: true });
    if (!newPlan) throw new Error('تولید برنامه ناموفق بود');
    if (!Array.isArray(newPlan.suggestions) || newPlan.suggestions.length === 0) {
      throw new Error('امروز ماموریت جدیدی برای تو ندارم. فردا دوباره بیا!');
    }

    const elapsed = Date.now() - startTime;
    if (elapsed < 7000) await new Promise((r) => setTimeout(r, 7000 - elapsed));

    const quotaUpdate = await coachApi.updateQuota(user.id, {
      freeUsed: 1,
      totalCoinsSpent: (coachQuota.totalCoinsSpent || 0) + firstCost,
    });
    if (!quotaUpdate) throw new Error('خطا در ثبت سهمیه');

    const saved = await coachApi.savePlan(user.id, newPlan, {
      generatedWith: firstCost > 0 ? 'paid' : 'free',
      coinsSpent: firstCost,
    });
    if (!saved?.id) {
      await coachApi.updateQuota(user.id, { freeUsed: 0 }).catch(() => {});
      throw new Error('خطا در ذخیره‌ی برنامه');
    }

    try {
      const conv = planToConversation(newPlan, user.id);
      if (conv) {
        await saveServerConversation(user.id, conv);
        setConversations((prev) => [conv, ...prev.filter((c) => c.id !== conv.id)]);
      }
    } catch (e) { console.warn('[coach] save conv failed:', e); }

    setTodayPlanId(saved.id);
    setTodayPlan(newPlan);
    setCoachQuota((prev) => ({
      ...prev,
      freeUsed: 1,
      totalCoinsSpent: (prev.totalCoinsSpent || 0) + firstCost,
    }));
  } catch (e) {
    console.error('[coach] generation failed:', e);
    if (coinsDeducted) {
      try { setUserRewards(originalCoins, originalXp); }
      catch (rb) { alert('خطا در بازگرداندن سکه'); throw e; }
    }
    alert(e?.message || 'خطا در دریافت برنامه');
    setShowCoachModal(false);
  } finally {
    setIsGenerating(false);
  }
}, [user, todayPlan, coachQuota.freeUsed, exams, isGenerating]);

// 🆕 همگام‌سازی ref با تابع فعلی (برای استفاده در effect پایین)
useEffect(() => {
  handleOpenCoachRef.current = handleOpenCoach;
}, [handleOpenCoach]);

// 🆕 کلیک خودکار — وقتی از StudentHome با state.autoCoach وارد شد
useEffect(() => {
  if (!canUseCoach) return; // 🆕 فقط دانش‌آموزان
  if (location.state?.autoCoach !== true) return;
  if (coachAutoTriggeredRef.current === location.key) return;
  coachAutoTriggeredRef.current = location.key;

  let cancelled = false;
  let t1 = null;
  let t2 = null;
  const startTime = Date.now();

  const attemptTrigger = () => {
    if (cancelled) return;
    const elapsed = Date.now() - startTime;
    const bootstrapped = coachBootstrappedRef.current;
    const maxWaitReached = elapsed > 1000;

    // اگر bootstrap هنوز تمام نشده و timeout نشده، دوباره چک کن
    if (!bootstrapped && !maxWaitReached) {
      t1 = setTimeout(attemptTrigger, 100);
      return;
    }

    const remainingWait = Math.max(0, 500 - elapsed);

    // بعد از ۵۰۰ms از ورود → شروع پالس
    t1 = setTimeout(() => {
      if (cancelled) return;
      setCoachAutoClicking(true);

      // بعد از ۵۰۰ms پالس → لغو کلاس + فراخوانی تابع
      t2 = setTimeout(() => {
        if (cancelled) return;
        setCoachAutoClicking(false);
        try { handleOpenCoachRef.current?.(); } catch (e) { console.warn('[autoCoach]', e); }
        // پاک کردن state تا رفرش/back دوباره trigger نکند
        navigate(location.pathname, { replace: true, state: {} });
      }, 500);
    }, remainingWait);
  };

  attemptTrigger();

  return () => {
    cancelled = true;
    if (t1) clearTimeout(t1);
    if (t2) clearTimeout(t2);
    setCoachAutoClicking(false);
  };
}, [location.key, location.state, navigate, canUseCoach]);

// ============================================================
// 🆕 شروع مأموریت (با snapshot برای تشخیص تکمیل)
// ============================================================

// ============================================================
// 🆕 شروع مأموریت (با snapshot برای تشخیص تکمیل)
// ============================================================
const handleStartMission = useCallback((sug) => {
  if (!user?.id) return;

  try {
    const pending = JSON.parse(localStorage.getItem(`aiCoach_pending_${user.id}`) || '{}');
    const snapshot = takeSnapshot(user.id, exams);
    pending[sug.id] = buildPendingItem(sug, snapshot);
    localStorage.setItem(`aiCoach_pending_${user.id}`, JSON.stringify(pending));
  } catch (e) {
    console.warn('[coach] save pending failed:', e);
  }

  try {
    const action = buildAction(sug, navigate);
    action();
  } catch (e) {
    console.warn('[coach] action error:', e);
  }

  setShowCoachModal(false);
}, [user?.id, exams, navigate]);

// ============================================================
// 🆕 پرداخت ۱۰۰ سکه + تولید مجدد + آپدیت گفتگو
// ============================================================
const handlePayAndGenerate = useCallback(async () => {
  if (!user?.id || isCoachPaying) return;

  const COST = Number(COACH_CONFIG.REGENERATE_COST) || 0;
  setIsCoachPaying(true);

  const originalCoins = coins;
  const originalXp = xp;
  let coinsDeducted = false;

  try {
    if (coins < COST) throw new Error(`سکه کافی نیست! حداقل ${COST.toLocaleString('fa-IR')} سکه نیاز داری.`);

    await new Promise((r) => setTimeout(r, 300));
    const spent = removeCoins(COST);
    if (spent !== COST) throw new Error('خطا در کسر سکه');
    coinsDeducted = true;

    const newPlan = await getDailyPlan(user.id, user.grade, user, exams, { forceRefresh: true });
    if (!newPlan) throw new Error('تولید برنامه ناموفق بود');
    if (!Array.isArray(newPlan.suggestions) || newPlan.suggestions.length === 0) {
      throw new Error('امروز ماموریت جدیدی برای تو ندارم. سکه‌ات برگشت.');
    }

    // 🆕 همیشه رکورد جدید (history حفظ)
    const saved = await coachApi.savePlan(user.id, newPlan, {
      generatedWith: 'paid',
      coinsSpent: COST,
    });
    if (!saved?.id) throw new Error('خطا در ذخیره‌ی برنامه');
    setTodayPlanId(saved.id);

    await coachApi.updateQuota(user.id, {
      paidCount: (coachQuota.paidCount || 0) + 1,
      totalCoinsSpent: (coachQuota.totalCoinsSpent || 0) + COST,
    });

    try {
      const conv = planToConversation(newPlan, user.id);
      if (conv) {
        await saveServerConversation(user.id, conv);
        setConversations((prev) => [conv, ...prev.filter((c) => c.id !== conv.id)]);
      }
    } catch (e) { console.warn('[coach] update conv failed:', e); }

    setTodayPlan(newPlan);
    setCoachProgress({});
    setCoachQuota((prev) => ({
      ...prev,
      paidCount: (prev.paidCount || 0) + 1,
      totalCoinsSpent: (prev.totalCoinsSpent || 0) + COST,
    }));
  } catch (e) {
    console.error('[coach] paid gen failed:', e);
    if (coinsDeducted) {
      try { setUserRewards(originalCoins, originalXp); }
      catch (rb) { alert('خطا در بازگرداندن سکه'); throw e; }
    }
    alert(e?.message || 'خطا در دریافت برنامه‌ی جدید');
    throw e;
  } finally {
    setIsCoachPaying(false);
  }
}, [user, coins, xp, isCoachPaying, todayPlanId, coachQuota, exams, removeCoins, setUserRewards]);








  // ============================================================
  // اگر کاربر لاگین نکرده
  // ============================================================
  if (!user) {
    return (
      <div className="ai-universe" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
        <div style={{ textAlign: 'center' }}>
          <FaRobot size={60} style={{ marginBottom: 20 }} />
          <h2>لطفاً ابتدا وارد شوید</h2>
          <p style={{ opacity: 0.7 }}>برای استفاده از دستیار هوشمند باید حساب کاربری داشته باشید.</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // رندر اصلی
  // ============================================================
  return (
    <div className="ai-universe">
      <div className="orb-bg"></div><div className="stars"></div><div className="floating-particles"></div>

      <SidebarComponent 
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        conversations={conversations}
        activeConversationId={activeConversationId}
        setActiveConversationId={setActiveConversationId}
        deleteConversation={deleteConversation}
        renameConversation={renameConversation}
        activeTooltip={activeTooltip}
        showTooltip={showTooltip}
        clearTooltipTimeout={clearTooltipTimeout}
        hideTooltip={hideTooltip}
        isPlanUnlocked={isPlanUnlocked}
        onBuyPlan={handleBuyPlan}
        subscription={subscription}
      />
      {imageUploadMenuJsx}

      <AnimatePresence>
        {showPaymentModal && (
          <PaymentModal
            plan={showPaymentModal.plan}
            period={payingPeriod}
            setPeriod={setPayingPeriod}
            isPaying={isPaying}
            onClose={() => !isPaying && setShowPaymentModal(null)}
            onPay={handlePay}
          />
        )}
      </AnimatePresence>

      <div className="glass-orb">
        <div className="header-3d">
          <div className="left-actions">
            <motion.button whileTap={{ scale: 0.9 }} className="icon-glass" onClick={openSidebar}><FiMenu /></motion.button>
          </div>
          <div className="brand">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}><FaRobot className="brand-icon" /></motion.div>
            <h1>آموزش‌یار <span className="gradient-text">یادو</span></h1>
            <motion.div className="plan-indicator" style={{ background: currentPlan.gradient }} whileHover={{ scale: 1.05 }}>{currentPlan.name}</motion.div>
          </div>
          <div className="right-actions">





            {/*<div className="remain-pill"><FiCheckCircle /><span>{remainingMessages} / {currentPlan.limit === 999999 ? '∞' : currentPlan.limit}</span></div>*/}










            <motion.button whileTap={{ scale: 0.9 }} className="icon-glass" onClick={handleCloseAI}><FiX /></motion.button>
          </div>
        </div>

        <div className="chat-container">
          <div className="messages-area">
            {messages.length === 0 && (
              <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="welcome-screen">
                <motion.div className="welcome-icon" animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }} transition={{ repeat: Infinity, duration: 3 }}><FiCompass /></motion.div>
                <h2>به سفر یادگیری خوش آمدی</h2>
                <p>از من هر سوال درسی بپرس، برنامه درسی بگیر یا وضعیت تحصیلی خود را تحلیل کن.</p>


{/* 🆕 دکمه‌ی مشاوره هوشمند — ۳ حالت */}
{canUseCoach && !todayPlan && (
  <motion.button
    className={`coach-cta ${coachQuota.freeUsed >= 1 ? 'cta-paid' : 'cta-free'}${coachAutoClicking ? ' auto-clicking' : ''}`}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={handleOpenCoach}
    disabled={isGenerating}
  >
    {coachQuota.freeUsed >= 1 ? (
      <><FiGift /><span>دریافت پیشنهاد جدید ({COACH_CONFIG.REGENERATE_COST.toLocaleString('fa-IR')} سکه)</span></>
    ) : Number(COACH_CONFIG.FIRST_REQUEST_COST) > 0 ? (
      <><FiGift /><span>مشاوره هوشمند امروز ({Number(COACH_CONFIG.FIRST_REQUEST_COST).toLocaleString('fa-IR')} سکه)</span></>
    ) : (
      <><FiStar /><span>مشاوره هوشمند امروز (رایگان)</span></>
    )}
  </motion.button>
)}

              </motion.div>
            )}
            {messages.map((msg, i) => <MessageItem key={i} msg={msg} idx={i} />)}
            {isLoading && (<div className="typing-ai"><span></span><span></span><span></span><div className="ai-thinking">دستیار در حال فکر کردن...</div></div>)}
          </div>

          {canUploadImage && imagePreview && (
            <div className="image-upload-area">
              <motion.div className="image-preview" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                <img src={imagePreview} alt="preview" />
                <motion.button className="remove-image" onClick={removeImage} whileHover={{ scale: 1.1 }}><FiX /></motion.button>
              </motion.div>
            </div>
          )}

          <ChatInput 
            input={input}
            setInput={setInput}
            sendMessage={sendMessage}
            isLoading={isLoading}
            remainingMessages={remainingMessages}
            maxChars={MAX_INPUT_CHARS[selectedPlan]}
            canUploadImage={canUploadImage}
            onImageClick={toggleImageMenu}
          />
        </div>

        {/*<div className="footer-note"><span>🔒 رمزنگاری شده | 🤖 {CONFIG.modelText} | ⚡ {currentPlan.name}</span></div>*/}
      </div>

      {/* 🆕 دکمه‌ی شناور مشاور (وقتی چت شروع شده) */}
{/* 🆕 FAB — فقط وقتی امروز پلن داری */}
{canUseCoach && todayPlan && (
  <motion.button
    className={`floating-coach${coachAutoClicking ? ' auto-clicking' : ''}`}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    animate={{ y: [0, -5, 0], transition: { repeat: Infinity, duration: 2 } }}
    onClick={handleOpenCoach}
    title="مشاوره هوشمند"
  >
    <FiStar />
    <span className="pulse-dot" />
  </motion.button>
)}

{/* 🆕 مودال مشاور — props-based */}
{canUseCoach && (
  <SmartCoachModal
    isOpen={showCoachModal}
    todayPlan={todayPlan}
    quota={coachQuota}
    stats={coachStats}
    progress={coachProgress}
    isPaying={isCoachPaying}
    isGenerating={isGenerating}
    regenCost={Number(COACH_CONFIG.REGENERATE_COST) || 100}
    rewardCoins={Number(COACH_CONFIG.DAILY_REWARD_COINS) || 0}
    rewardClaimed={dailyRewardClaimed}
    onClose={() => setShowCoachModal(false)}
    onStartMission={handleStartMission}
    onPayAndGenerate={handlePayAndGenerate}
  />
)}

<motion.button className="floating-new" whileHover={{ scale: 1.1, rotate: 90 }} whileTap={{ scale: 0.9 }} animate={{ y: [0, -5, 0], transition: { repeat: Infinity, duration: 2 } }} onClick={newConversation}><FiPlus /></motion.button>
    </div>
  );
}